import React, { forwardRef, type ReactNode } from 'react'
import { TextInput, View } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { typographyVariants, variantForStyleId } from '../../typography/variants'
import { AdditionalText } from '../additionaltext/AdditionalText'
import { Label } from '../label/Label'
import { inputContract } from './input.contract'

const VARIANTS = inputContract.props.variant.values

export type InputVariant = (typeof VARIANTS)[number]

export const INPUT_TEXT = variantForStyleId(inputContract.textStyleIds[0])

type ContractCell = {
    container: string
    label: string
    box: string
    icon: string
    parts: Record<string, string>
}

const cellFor = (variant: InputVariant): ContractCell => {
    const key = `variant=${variant}`
    const found = (inputContract.variants as Record<string, ContractCell>)[key]
    if (!found) {
        throw new Error(`aos-ds Input: the Figma contract has no "${key}"`)
    }
    return found
}

export type InputProps = Omit<
    React.ComponentProps<typeof TextInput>,
    'style' | 'className' | 'multiline'
> & {
    /**
     * Figma's five. `not-filled` and `filled` differ only in the ink; `focus`,
     * `error` and `disabled` are the states the file draws. Hover is not among
     * them — the contract folds it into `hover:` classes, because a web
     * component should not be told when the pointer is over it.
     */
    variant?: InputVariant
    /** Figma's `showLabel`, the `Label` this component places above the field. */
    label?: string
    required?: boolean
    /** Figma's `showAdditionalText`, the hint or error under the field. */
    additionalText?: string
    /** Figma's `iconLeft` / `iconRight`, both INSTANCE_SWAP — the caller's glyph. */
    iconLeft?: ReactNode
    iconRight?: ReactNode
    className?: string
    testID?: string
}

type InputRef = React.ComponentRef<typeof TextInput>

/**
 * A labelled single-line field: a `Label`, the field, and an `AdditionalText`
 * under it. All three are components this package ships, and
 * `inputContract.composes` names them.
 *
 * `container` is the stack and `box` is the surface — Figma paints the field on
 * an `input` child inside a stack that also holds the label and the hint, so
 * putting both class sets on one element renders the label inside the field.
 * `TextAreaInput` is the same shape and carries the same note.
 *
 * The disabled opacity lives in `container` rather than in `disabled`. Figma
 * spells this component's `disabled` as one value of the `variant` axis, beside
 * `filled` and `error`, so it is a cell rather than a state — and reading it as
 * a state is how `TextAreaInput` shipped rendering its disabled field at full
 * strength.
 */
export const Input = forwardRef<InputRef, InputProps>(
    (
        {
            variant = 'not-filled',
            label,
            required,
            additionalText,
            iconLeft,
            iconRight,
            className,
            testID,
            ...rest
        },
        ref,
    ) => {
        const cell = cellFor(variant)
        const disabled = variant === 'disabled'
        return (
            <View
                className={twMerge('flex-col', cell.container, className)}
                testID={testID ?? `input-${variant}`}
            >
                {label ? <Label required={required}>{label}</Label> : null}
                <View className={twMerge('w-full flex-row', cell.box)} testID='input-box'>
                    <View className={twMerge('flex-1 flex-row', cell.parts.content)}>
                        {iconLeft ? <View className={cell.icon}>{iconLeft}</View> : null}
                        <TextInput
                            ref={ref}
                            editable={!disabled}
                            className={twMerge(
                                'flex-1',
                                typographyVariants[INPUT_TEXT],
                                cell.parts['Help text'],
                            )}
                            testID='input-field'
                            {...rest}
                        />
                    </View>
                    {iconRight ? (
                        <View className={twMerge('flex-row', cell.icon, cell.parts['right-icons'])}>
                            {iconRight}
                        </View>
                    ) : null}
                </View>
                {additionalText ? (
                    <AdditionalText variant={variant === 'error' ? 'error' : 'default'}>
                        {additionalText}
                    </AdditionalText>
                ) : null}
            </View>
        )
    },
)

Input.displayName = 'Input'

export { VARIANTS as INPUT_VARIANTS }
